import type { ReactNode } from 'react';

import type { Value } from '@spotify-confidence/sdk';

import { useLocalOverrideInterceptor } from './localOverride';

export type FlagEvaluationAttributes = Record<
  string,
  string | boolean | number | undefined
>;

export type FlagValueResolver = <T extends Value>(
  flag: string,
  defaultValue: T,
) => T;

export type FlagValueInterceptor = <T extends Value>(
  flag: string,
  defaultValue: T,
  resolvedValue: T,
) => T;

export type EvaluationContextSetter = (
  attributes: FlagEvaluationAttributes,
) => void;

export type EvaluationContextInterceptor = (
  setEvaluationContext: EvaluationContextSetter,
) => EvaluationContextSetter;

export type FlagContentRenderer = (
  content: ReactNode,
  flag: string,
  enabled: boolean,
) => ReactNode;

export type FlagContentInterceptor = (
  render: FlagContentRenderer,
) => FlagContentRenderer;

export type FlagInterceptors = {
  value: readonly FlagValueInterceptor[];
  evaluationContext: EvaluationContextInterceptor;
  content: FlagContentInterceptor;
};

const DEFAULT_INTERCEPTORS: FlagInterceptors = {
  value: [useLocalOverrideInterceptor],
  evaluationContext: setEvaluationContext => setEvaluationContext,
  content: render => render,
};

let interceptors: FlagInterceptors = DEFAULT_INTERCEPTORS;

type Wrapper<T> = (inner: T) => T;

const composedWith = <T>(
  registered: Wrapper<T> | undefined,
  existing: Wrapper<T>,
): Wrapper<T> =>
  registered === undefined ? existing : base => existing(registered(base));

export const registerFlagInterceptors = (
  registered: Partial<FlagInterceptors>,
): void => {
  interceptors = {
    value: [...(registered.value ?? []), ...interceptors.value],
    evaluationContext: composedWith(
      registered.evaluationContext,
      interceptors.evaluationContext,
    ),
    content: composedWith(registered.content, interceptors.content),
  };
};

export const resetFlagInterceptors = (): void => {
  interceptors = DEFAULT_INTERCEPTORS;
};

const lazily = <T>(build: () => T): (() => T) => {
  let built: { value: T } | undefined;
  return () => (built ??= { value: build() }).value;
};

const interceptedBy =
  (
    useInner: FlagValueResolver,
    useInterceptor: FlagValueInterceptor,
  ): FlagValueResolver =>
  <T extends Value>(flag: string, defaultValue: T): T =>
    useInterceptor(flag, defaultValue, useInner(flag, defaultValue));

export const interceptFlagValue = (
  useResolvedFlagValue: FlagValueResolver,
): FlagValueResolver => {
  const resolver = lazily(() =>
    interceptors.value.reduce(interceptedBy, useResolvedFlagValue),
  );

  return <T extends Value>(flag: string, defaultValue: T): T =>
    resolver()(flag, defaultValue);
};

export const interceptEvaluationContext = (
  setEvaluationContext: EvaluationContextSetter,
): EvaluationContextSetter => {
  const setter = lazily(() =>
    interceptors.evaluationContext(setEvaluationContext),
  );

  return (attributes: FlagEvaluationAttributes): void => setter()(attributes);
};

export const interceptFlaggedContent = (
  renderContent: FlagContentRenderer = content => content,
): FlagContentRenderer => {
  const render = lazily(() => interceptors.content(renderContent));

  return (content: ReactNode, flag: string, enabled: boolean): ReactNode =>
    render()(content, flag, enabled);
};
